/**
 * Exchange Volume configuration - view constants, exchange metadata,
 * asset class / underlying class taxonomy, storage keys, and default filter state.
 */
import React from 'react';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import PaymentsIcon from '@mui/icons-material/Payments';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
// Asset class group icons
import GrainIcon from '@mui/icons-material/Grain';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import HubIcon from '@mui/icons-material/Hub';
import SavingsIcon from '@mui/icons-material/Savings';
// Underlying class icons
import GrassIcon from '@mui/icons-material/Grass';
import InventoryIcon from '@mui/icons-material/Inventory';
import BoltIcon from '@mui/icons-material/Bolt';
import HardwareIcon from '@mui/icons-material/Hardware';
import BarChartIcon from '@mui/icons-material/BarChart';
import BlenderIcon from '@mui/icons-material/Blender';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WavesIcon from '@mui/icons-material/Waves';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import PercentIcon from '@mui/icons-material/Percent';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import DescriptionIcon from '@mui/icons-material/Description';

// ============================================
// Views
// ============================================

export const EXCHANGE_VOLUME_VIEWS = {
  TRENDS: 'trends',
  TABLE: 'table',
  MARKET_SHARE: 'marketShare',
};

export const EXCHANGE_VOLUME_VIEW_STORAGE_KEY = 'marketInsightsExchangeVolumeView';
export const EXCHANGE_VOLUME_VIEW_EVENT_NAME = 'marketInsightsExchangeVolumeViewChange';

// ============================================
// Exchange Metadata
// ============================================

export const EXCHANGES = {
  ASX:  { ticker: 'ASX',  name: 'ASX Ltd',                   color: 'rgb(218, 165, 32)' },
  CBOE: { ticker: 'CBOE', name: 'Cboe Global Markets',       color: 'rgb(82, 133, 62)' },
  CME:  { ticker: 'CME',  name: 'CME Group',                 color: 'rgb(95, 116, 155)' },
  DB1:  { ticker: 'DB1',  name: 'Deutsche Börse',            color: 'rgb(70, 130, 180)' },
  ENXT: { ticker: 'ENXT', name: 'Euronext',                  color: 'rgb(106, 90, 205)' },
  HKEX: { ticker: 'HKEX', name: 'Hong Kong Exchanges',       color: 'rgb(178, 102, 178)' },
  ICE:  { ticker: 'ICE',  name: 'Intercontinental Exchange',  color: 'rgb(192, 107, 86)' },
  JPX:  { ticker: 'JPX',  name: 'Japan Exchange Group',      color: 'rgb(220, 120, 120)' },
  LSEG: { ticker: 'LSEG', name: 'London Stock Exchange Group', color: 'rgb(0, 128, 128)' },
  MKTX: { ticker: 'MKTX', name: 'MarketAxess Holdings',      color: 'rgb(200, 142, 63)' },
  NDAQ: { ticker: 'NDAQ', name: 'Nasdaq, Inc.',              color: 'rgb(142, 193, 122)' },
  TMX:  { ticker: 'TMX',  name: 'TMX Group',                 color: 'rgb(169, 140, 100)' },
  TW:   { ticker: 'TW',   name: 'Tradeweb Markets',          color: 'rgb(145, 166, 205)' },
  B3:   { ticker: 'B3',   name: 'B3 — Brasil Bolsa Balcão',  color: 'rgb(0, 166, 81)' },
  LME:  { ticker: 'LME',  name: 'London Metal Exchange',     color: 'rgb(120, 90, 60)' },
  SGX:  { ticker: 'SGX',  name: 'Singapore Exchange',        color: 'rgb(220, 60, 60)' },
  TRMD: { ticker: 'TRMD', name: 'Trend Micro / TRMD',        color: 'rgb(80, 170, 200)' },
};

export const EXCHANGE_LIST = Object.keys(EXCHANGES);

// ============================================
// Metric Options
// ============================================

export const METRICS = {
  volume: { key: 'volume', label: 'Total Volume' },
  adv:    { key: 'adv',    label: 'Avg Daily Volume' },
};

// ============================================
// Asset Class / Underlying Class Taxonomy
// ============================================

/**
 * Parent groups (asset classes from JSON data).
 * IDs: 1-6 (parent level).
 */
export const ASSET_CLASS_GROUPS = [
  { id: 1, label: 'Commodities',            key: 'commodities',            childIds: [10, 11, 12, 13], icon: React.createElement(GrainIcon, { sx: { fontSize: 14 } }) },
  { id: 2, label: 'Equities',               key: 'equities',              childIds: [20, 21, 22, 23], icon: React.createElement(ShowChartIcon, { sx: { fontSize: 14 } }) },
  { id: 3, label: 'Fixed Income',            key: 'fixed_income',          childIds: [30, 31],         icon: React.createElement(AccountBalanceIcon, { sx: { fontSize: 14 } }) },
  { id: 4, label: 'FX',                      key: 'fx',                    childIds: [40],             icon: React.createElement(CurrencyExchangeIcon, { sx: { fontSize: 14 } }) },
  { id: 5, label: 'Market Infrastructure',   key: 'market_infrastructure', childIds: [50, 51],         icon: React.createElement(HubIcon, { sx: { fontSize: 14 } }) },
  { id: 6, label: 'Money Markets',           key: 'money_markets',         childIds: [60],             icon: React.createElement(SavingsIcon, { sx: { fontSize: 14 } }) },
];

/**
 * Child entries (underlying classes from JSON data).
 * IDs: 10-13, 20-23, 30-31, 40, 50-51, 60.
 */
export const UNDERLYING_CLASS_CATEGORIES = [
  // Commodities children
  { id: 10, label: 'Agriculture', key: 'agriculture', parentId: 1, icon: React.createElement(GrassIcon, { sx: { fontSize: 14 } }) },
  { id: 11, label: 'Commodity',   key: 'commodity',   parentId: 1, icon: React.createElement(InventoryIcon, { sx: { fontSize: 14 } }) },
  { id: 12, label: 'Energy',      key: 'energy',      parentId: 1, icon: React.createElement(BoltIcon, { sx: { fontSize: 14 } }) },
  { id: 13, label: 'Metals',      key: 'metals',      parentId: 1, icon: React.createElement(HardwareIcon, { sx: { fontSize: 14 } }) },
  // Equities children
  { id: 20, label: 'Equity Index',  key: 'equity_index',  parentId: 2, icon: React.createElement(BarChartIcon, { sx: { fontSize: 14 } }) },
  { id: 21, label: 'Mixed',         key: 'mixed',         parentId: 2, icon: React.createElement(BlenderIcon, { sx: { fontSize: 14 } }) },
  { id: 22, label: 'Single Stock',  key: 'single_stock',  parentId: 2, icon: React.createElement(ReceiptLongIcon, { sx: { fontSize: 14 } }) },
  { id: 23, label: 'Volatility',    key: 'volatility',    parentId: 2, icon: React.createElement(WavesIcon, { sx: { fontSize: 14 } }) },
  // Fixed Income children
  { id: 30, label: 'Credit', key: 'credit', parentId: 3, icon: React.createElement(CreditScoreIcon, { sx: { fontSize: 14 } }) },
  { id: 31, label: 'Rates',  key: 'rates',  parentId: 3, icon: React.createElement(PercentIcon, { sx: { fontSize: 14 } }) },
  // FX children
  { id: 40, label: 'FX', key: 'fx', parentId: 4, icon: React.createElement(CurrencyExchangeIcon, { sx: { fontSize: 14 } }) },
  // Market Infrastructure children
  { id: 50, label: 'Funds',      key: 'funds',      parentId: 5, icon: React.createElement(FolderSpecialIcon, { sx: { fontSize: 14 } }) },
  { id: 51, label: 'Securities', key: 'securities', parentId: 5, icon: React.createElement(DescriptionIcon, { sx: { fontSize: 14 } }) },
  // Money Markets children
  { id: 60, label: 'Repo', key: 'repo', parentId: 6, icon: React.createElement(SwapHorizIcon, { sx: { fontSize: 14 } }) },
];

/** All child underlying class IDs */
export const ALL_UNDERLYING_IDS = UNDERLYING_CLASS_CATEGORIES.map((c) => c.id);

/** Free tier: Credit only (underlying class id 30 within Fixed Income) */
export const FREE_ASSET_CLASS_IDS = new Set([30]);

/** Free tier default asset class selection */
export const FREE_TIER_ASSET_CLASS_SELECTION = [30];

/**
 * Returns true if any selected child IDs are outside the free tier (Credit only).
 * Only checks child IDs (>= 10), ignoring parent IDs (1-6).
 */
export function hasNonFreeAssetClasses(selection) {
  return selection.some((id) => id >= 10 && !FREE_ASSET_CLASS_IDS.has(id));
}

/** All parent + child IDs combined (for initial "select all" state) */
export const ALL_IDS_WITH_PARENTS = [
  ...ASSET_CLASS_GROUPS.map((p) => p.id),
  ...ALL_UNDERLYING_IDS,
];

/**
 * Numeric ID → underlying class string key lookup.
 */
export function getUnderlyingClassKey(id) {
  const cat = UNDERLYING_CLASS_CATEGORIES.find((c) => c.id === id);
  return cat ? cat.key : null;
}

/**
 * Underlying class key → label lookup.
 */
export function getUnderlyingClassLabel(key) {
  const cat = UNDERLYING_CLASS_CATEGORIES.find((c) => c.key === key);
  return cat ? cat.label : null;
}

/**
 * Asset class key → label lookup.
 */
export function getAssetClassLabel(key) {
  const group = ASSET_CLASS_GROUPS.find((g) => g.key === key);
  return group ? group.label : null;
}

/**
 * Handle asset class / underlying class toggle with parent/child cascade logic.
 * Mirrors handleCategoryToggle from filterCategories.js.
 *
 * @param {number[]} selectedItems - New selection from the event
 * @param {number[]} prev - Previous selection state
 * @returns {number[]} Updated selection
 */
export function handleAssetClassToggle(selectedItems, prev) {
  let newState = [...selectedItems];

  ASSET_CLASS_GROUPS.forEach((parent) => {
    const wasChecked = prev.includes(parent.id);
    const isChecked = selectedItems.includes(parent.id);

    if (isChecked && !wasChecked) {
      // Parent just checked → add all children
      parent.childIds.forEach((cid) => {
        if (!newState.includes(cid)) newState.push(cid);
      });
    } else if (!isChecked && wasChecked) {
      // Parent just unchecked → remove all children
      newState = newState.filter((id) => !parent.childIds.includes(id));
    } else {
      // Parent wasn't toggled — check child state
      const allChildrenChecked = parent.childIds.every((cid) => newState.includes(cid));
      const noChildrenChecked = parent.childIds.every((cid) => !newState.includes(cid));

      if (allChildrenChecked && !newState.includes(parent.id)) {
        newState.push(parent.id);
      } else if (noChildrenChecked && newState.includes(parent.id)) {
        newState = newState.filter((id) => id !== parent.id);
      } else if (!allChildrenChecked && newState.includes(parent.id)) {
        newState = newState.filter((id) => id !== parent.id);
      }
    }
  });

  return newState;
}

// ============================================
// Product Type Options (SimpleSelect)
// ============================================

export const PRODUCT_TYPE_OPTIONS = [
  { value: 'all',      label: 'All',      icon: React.createElement(SelectAllIcon, { sx: { fontSize: 14 } }) },
  { value: 'cash',     label: 'Cash',     icon: React.createElement(PaymentsIcon, { sx: { fontSize: 14 } }) },
  { value: 'clearing', label: 'Clearing', icon: React.createElement(SyncAltIcon, { sx: { fontSize: 14 } }) },
  { value: 'etf',      label: 'ETF',      icon: React.createElement(AccountBalanceIcon, { sx: { fontSize: 14 } }) },
  { value: 'futures',  label: 'Futures',  icon: React.createElement(TrendingUpIcon, { sx: { fontSize: 14 } }) },
  { value: 'options',  label: 'Options',  icon: React.createElement(TuneIcon, { sx: { fontSize: 14 } }) },
  { value: 'swaps',    label: 'Swaps',    icon: React.createElement(SwapHorizIcon, { sx: { fontSize: 14 } }) },
];

// ============================================
// Exchange Options (SimpleSelect)
// ============================================

export const EXCHANGE_OPTIONS = [
  { value: 'all', label: 'All Exchanges', icon: React.createElement(PublicIcon, { sx: { fontSize: 14 } }) },
  ...EXCHANGE_LIST.map((key) => ({ value: key, label: EXCHANGES[key].name, icon: React.createElement(BusinessIcon, { sx: { fontSize: 14 } }) })),
];

// ============================================
// Underlying Class Colors (for chart series)
// ============================================

export const UNDERLYING_CLASS_COLORS = {
  agriculture:  'rgb(210, 180, 80)',   // dark goldenrod
  commodity:    'rgb(255, 140, 0)',    // dark orange
  energy:       'rgb(255, 99, 71)',    // tomato
  metals:       'rgb(184, 134, 11)',   // dark goldenrod
  equity_index: 'rgb(46, 139, 87)',    // sea green
  mixed:        'rgb(102, 205, 170)',  // medium aquamarine
  single_stock: 'rgb(60, 179, 113)',   // medium sea green
  volatility:   'rgb(178, 102, 178)',  // medium orchid
  credit:       'rgb(70, 130, 180)',   // steel blue
  rates:        'rgb(100, 149, 237)',  // cornflower blue
  fx:           'rgb(147, 112, 219)',  // medium purple
  funds:        'rgb(169, 169, 169)',  // dark gray
  securities:   'rgb(135, 206, 235)',  // sky blue
  repo:         'rgb(135, 206, 235)',  // sky blue
};

export function getUnderlyingClassColor(key) {
  return UNDERLYING_CLASS_COLORS[key] || 'rgb(145, 166, 205)';
}

// ============================================
// Default Filter State
// ============================================

export const DEFAULT_FILTERS = {
  assetClassSelection: [...ALL_IDS_WITH_PARENTS],
  productType: 'all',
  exchange: 'all',
  metric: 'adv',
  dateRange: { start: null, end: null },
};

// ============================================
// Calculation Options (for table sub-rows)
// ============================================

export const EXCHANGE_CALC_OPTIONS = [
  { value: 'momPct', label: 'MoM %' },
  { value: 'qoqPct', label: 'QoQ %' },
  { value: 'yoyPct', label: 'YoY %' },
];

export const CALC_PLACEMENT_MODES = {
  INLINE: 'inline',
  BOTTOM: 'bottom',
};

export const DEFAULT_MONTHS_DISPLAY = 12;
export const DEFAULT_SHARE_MONTHS_DISPLAY = 120;
export const MIN_MONTHS_DISPLAY = 3;

// ============================================
// AG Grid Numerical Fields (for mean/median)
// ============================================

export const TABLE_NUMERICAL_FIELDS = ['latestValue', 'priorValue', 'mom', 'yoy'];

// ============================================
// Unit Families (for cascading market share filters)
// ============================================

export const UNIT_FAMILIES = {
  contracts: { label: 'Contracts', units: ['contracts'] },
  notional:  { label: 'Notional',  units: ['usd', 'eur', 'aud', 'gbp', 'hkd', 'rmb', 'sgd', 'brl', 'cad'] },
  energy:    { label: 'Energy',    units: ['mwh', 'twh'] },
  shares:    { label: 'Shares',    units: ['shares'] },
  count:     { label: 'Count',     units: ['trades', 'count', 'transactions', 'companies'] },
};

/**
 * Determine which unit family a baseUnit belongs to.
 * @param {string} baseUnit - e.g. 'contracts', 'usd', 'mwh'
 * @returns {string|null} Family key or null if unknown
 */
export function getUnitFamily(baseUnit) {
  if (!baseUnit) return null;
  const lower = baseUnit.toLowerCase();
  for (const [familyKey, family] of Object.entries(UNIT_FAMILIES)) {
    if (family.units.includes(lower)) return familyKey;
  }
  return null;
}

/**
 * Get available unit families from a set of products, sorted by product count descending.
 * @param {Array} products - Array of product objects with baseUnit
 * @returns {Array} [{ key, label, count }]
 */
export function getAvailableUnitFamilies(products) {
  const familyCounts = {};
  products.forEach((prod) => {
    const family = getUnitFamily(prod.baseUnit);
    if (family) {
      familyCounts[family] = (familyCounts[family] || 0) + 1;
    }
  });
  return Object.entries(familyCounts)
    .map(([key, count]) => ({ key, label: UNIT_FAMILIES[key].label, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Get available product types for a given dimension selection.
 * Returns product types that actually have data, sorted by total volume descending.
 * @param {Array} providers - Normalized providers array
 * @param {string} dimensionType - 'assetClass' or 'underlyingClass'
 * @param {string} dimensionValue - The selected value (e.g. 'credit', 'commodities')
 * @returns {Array} [{ value, label, volume }]
 */
export function getAvailableProductTypes(providers, dimensionType, dimensionValue) {
  const typeTotals = {};

  providers.forEach((prov) => {
    prov.products.forEach((prod) => {
      const match = dimensionType === 'underlyingClass'
        ? prod.underlyingClass === dimensionValue
        : prod.assetClass === dimensionValue;
      if (!match) return;

      const pt = prod.productType;
      if (!typeTotals[pt]) typeTotals[pt] = 0;
      const values = prod.volume || [];
      typeTotals[pt] += values.reduce((s, v) => s + (v || 0), 0);
    });
  });

  return Object.entries(typeTotals)
    .map(([value, volume]) => ({
      value,
      label: value.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      volume,
    }))
    .sort((a, b) => b.volume - a.volume);
}
